import { initializeApp } from "firebase/app";
// import { getDatabase } from "firebase/database";
import {
    getAuth,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    sendPasswordResetEmail,
    signOut,
} from "firebase/auth";
import {
  query,
  getDocs,
  where,
  initializeFirestore,
  collection,
  deleteDoc,
  orderBy,
  doc,
  getDoc,
  setDoc,
  limit as firestoreLimit,
  writeBatch,
  startAfter,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, getStorage } from "firebase/storage";

// Default: DEV (microcuotas-dev). Se puede overridear por .env (REACT_APP_FIREBASE_*).
const defaultFirebaseConfig = {
  apiKey: "AIzaSyA34nXpHgcCSrD6ztqLW9dCtsXTj5wo3ww",
  authDomain: "microcuotas-dev.firebaseapp.com",
  projectId: "microcuotas-dev",
  storageBucket: "microcuotas-dev.firebasestorage.app",
  messagingSenderId: "591172272146",
  appId: "1:591172272146:web:c2587b31ecaf0a833618cb",
  measurementId: "G-CYV2NJKQ7P",
};

const resolveFirebaseConfig = () => {
  const overrides = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
    measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
  };

  const sanitizedOverrides = Object.entries(overrides).reduce((acc, [key, value]) => {
    if (typeof value === "string" && value.trim().length > 0) {
      acc[key] = value.trim();
    }
    return acc;
  }, {});

  const mergedConfig = { ...defaultFirebaseConfig, ...sanitizedOverrides };

  const requiredKeys = ["apiKey", "authDomain", "projectId", "appId"];
  const missingKeys = requiredKeys.filter((key) => !mergedConfig[key]);
  if (missingKeys.length) {
    console.warn(
      `Firebase config missing keys: ${missingKeys.join(
        ", "
      )}. Check your .env (REACT_APP_FIREBASE_* variables).`
    );
  }

  return mergedConfig;
};

const firebaseConfig = resolveFirebaseConfig();

const app = initializeApp(firebaseConfig);
// const db = getDatabase(app);
const auth = getAuth(app);
const storage = getStorage(app);
const forceLongPolling = String(process.env.REACT_APP_FIREBASE_FORCE_LONG_POLLING || "").toLowerCase() === "true";
const firestoreSettings = {
  // Ayuda en redes/proxies donde el stream WebChannel falla (errores Listen/channel).
  // Importante: `experimentalForceLongPolling` y `experimentalAutoDetectLongPolling` NO pueden configurarse juntos
  // (incluso si uno es false) o Firestore lanza: "cannot be used together".
  useFetchStreams: false,
};
if (forceLongPolling) {
  firestoreSettings.experimentalForceLongPolling = true;
} else {
  firestoreSettings.experimentalAutoDetectLongPolling = true;
}
const db = initializeFirestore(app, firestoreSettings);


const logInWithEmailAndPassword = async (email, password) => {
  try {
      await signInWithEmailAndPassword(auth, email, password);
      try {
        await ensureUserProfileDocument(auth.currentUser);
      } catch (error) {
        console.warn("No se pudo sincronizar el perfil del usuario:", error);
      }
  } catch (err) {
      console.error(err);
      alert(err.message);
  }
};
const registerWithEmailAndPassword = async (name, email, password, role) => {
try {
  const res = await createUserWithEmailAndPassword(auth, email, password);
  const user = res.user;
  await setDoc(
    doc(db, "users", user.uid),
    {
      uid: user.uid,
      name,
      authProvider: "local",
      email,
      role,
    },
    { merge: true }
  );
} catch (err) {
  console.error(err);
  alert(err.message);
}
};

const ensureUserProfileDocument = async (user) => {
  const uid = user?.uid;
  if (!uid) {
    return null;
  }

  const primaryRef = doc(db, "users", uid);
  const primarySnap = await getDoc(primaryRef);
  if (primarySnap.exists()) {
    return { id: primarySnap.id, ...primarySnap.data() };
  }

  const q = query(collection(db, "users"), where("uid", "==", uid), firestoreLimit(1));
  const snapshot = await getDocs(q);
  const fallbackDoc = snapshot.docs.length ? snapshot.docs[0] : null;
  const fallbackData = fallbackDoc?.data?.() || {};

  const profile = {
    uid,
    role: fallbackData.role || "user",
    name: fallbackData.name || user.displayName || "",
    authProvider: fallbackData.authProvider || "local",
    email: fallbackData.email || user.email || "",
  };

  await setDoc(primaryRef, profile, { merge: true });
  return profile;
};

const isAdmin = async (uid) => {
  if (!uid) {
    return false;
  }
  const primarySnap = await getDoc(doc(db, "users", uid));
  if (primarySnap.exists()) {
    return primarySnap.data()?.role === "admin";
  }
  const q = query(collection(db, "users"), where("uid", "==", uid), firestoreLimit(1));
  const snap = await getDocs(q);
  if (!snap.docs.length) {
    return false;
  }
  return snap.docs[0].data()?.role === "admin";
};

const isReport = async (uid) => {
  if (!uid) {
    return false;
  }
  const primarySnap = await getDoc(doc(db, "users", uid));
  if (primarySnap.exists()) {
    return primarySnap.data()?.role === "report";
  }
  const q = query(collection(db, "users"), where("uid", "==", uid), firestoreLimit(1));
  const snap = await getDocs(q);
  if (!snap.docs.length) {
    return false;
  }
  return snap.docs[0].data()?.role === "report";
};  

const fetchContactsData = async (startDate, endDate, extraOptions = {}) => {
  // Backwards compatible signature: fetchContactsData(startDate, endDate)
  // New signature: fetchContactsData({ startDate, endDate, limit, startAfter, withCursor })
  const resolvedOptions =
    typeof startDate === "object" && startDate !== null && !Array.isArray(startDate)
      ? { ...startDate }
      : { startDate, endDate, ...extraOptions };

  const {
    startDate: start,
    endDate: end,
    limit: limitSize,
    startAfter: startAfterDoc,
    withCursor = false,
  } = resolvedOptions;

  const constraints = [orderBy("timestamp", "desc")];

  if (start && !end) {
    constraints.push(where("timestamp", ">=", new Date(start)));
  } else if (!start && end) {
    constraints.push(where("timestamp", "<=", new Date(end)));
  } else if (start && end) {
    constraints.push(where("timestamp", ">=", new Date(start)));
    constraints.push(where("timestamp", "<=", new Date(end)));
  }

  if (limitSize) {
    if (startAfterDoc) {
      constraints.push(startAfter(startAfterDoc));
    }
    constraints.push(firestoreLimit(limitSize));
  }

  const q = query(collection(db, "clientes"), ...constraints);

  const querySnapshot = await getDocs(q);
  const data = querySnapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));

  if (withCursor) {
    const lastDoc = querySnapshot.docs.length
      ? querySnapshot.docs[querySnapshot.docs.length - 1]
      : null;
    return { rows: data, lastDoc };
  }

  return data;
};

const deleteOldClientesBefore = async (cutoffDate, { batchSize = 500 } = {}) => {
  if (!cutoffDate) {
    return { deleted: 0, hasMore: false };
  }

  const cutoff = cutoffDate instanceof Date ? cutoffDate : new Date(cutoffDate);
  if (Number.isNaN(cutoff.getTime())) {
    return { deleted: 0, hasMore: false };
  }

  const constraints = [
    orderBy("timestamp", "asc"),
    where("timestamp", "<", cutoff),
    firestoreLimit(batchSize),
  ];

  const q = query(collection(db, "clientes"), ...constraints);
  const snapshot = await getDocs(q);
  if (snapshot.empty) {
    return { deleted: 0, hasMore: false };
  }

  const batch = writeBatch(db);
  snapshot.docs.forEach((docSnap) => {
    batch.delete(doc(db, "clientes", docSnap.id));
  });
  await batch.commit();

  const deleted = snapshot.size;
  const hasMore = deleted >= batchSize;
  return { deleted, hasMore };
};

const SIMULATION_PARAMS_PATH = ["config", "simulationParams"];

const getSimulationParams = async () => {
  const docRef = doc(db, ...SIMULATION_PARAMS_PATH);
  const snap = await getDoc(docRef);

  if (!snap.exists()) {
    return {
      minCuotas: 2,
      maxCuotas: 12,
      minMonto: 50000,
      maxMonto: 500000,
      whatsappPhone: "5491123214014",
      interesesPorCuota: {}
    };
  }

  return snap.data();
};

const updateSimulationParams = async (params) => {
  const docRef = doc(db, ...SIMULATION_PARAMS_PATH);
  await setDoc(docRef, params, { merge: true });
};

const logout = () => {
signOut(auth);
};

export {
  auth,
  db,
  storage,
  deleteDoc,
  ref,
  uploadBytes,
  getDownloadURL,
  logInWithEmailAndPassword,
  registerWithEmailAndPassword,
  ensureUserProfileDocument,
  sendPasswordResetEmail,
  logout,
  isAdmin,
  isReport,
  fetchContactsData,
  deleteOldClientesBefore,
  getSimulationParams,
  updateSimulationParams
};
export default firebaseConfig;

